/**
 * @typedef {Object} VisibleRowsChangePayload
 * @property {number} startIndex
 * @property {number} stopIndex
 * @property {Array<object>} visibleModels
 */

/**
 * @callback OnVisibleRowsChange
 * @param {VisibleRowsChangePayload} payload
 * @returns {void}
 */

/**
 * @typedef {Object} VisibleRowsObserverOptions
 * @property {() => (Element|null)} getRoot
 * @property {() => Element[]} getRowElements
 * @property {(element: Element) => (string|null)} getRowIdFromElement
 */

/**
 * Visible row tracking for non-virtualized tables.
 */
export default class VisibleRowsObserver {
  /**
   * @param {VisibleRowsObserverOptions} options
   */
  constructor({ getRoot, getRowElements, getRowIdFromElement }) {
    this.getRoot = getRoot;
    this.getRowElements = getRowElements;
    this.getRowIdFromElement = getRowIdFromElement;

    this.observer = null;
    this.observerRoot = null;
    this.visibleRowIds = new Set();
    this.observedRowElements = new Map();
    this.lastVisibleRowIds = [];
    this.fallbackEmitted = false;

    this.onVisibleRowsChange = null;
    this.collection = null;
    this.modelField = 'models';
  }

  update = ({ onVisibleRowsChange, collection, modelField }) => {
    /** @type {OnVisibleRowsChange|null} */
    if (this.onVisibleRowsChange !== onVisibleRowsChange) {
      this.fallbackEmitted = false;
    }
    this.onVisibleRowsChange = onVisibleRowsChange;
    this.collection = collection;
    this.modelField = modelField || 'models';
  };

  ensureObserverRoot = () => {
    const nextRoot = this.getRoot();

    if (!this.onVisibleRowsChange) {
      if (this.observer) {
        this.teardown();
      }
      return;
    }

    if (this.observer && nextRoot === this.observerRoot) {
      return;
    }

    if (!this.observer) {
      this.setupObserver(nextRoot);
      return;
    }

    if (nextRoot !== this.observerRoot) {
      this.teardown();
      this.setupObserver(nextRoot);
    }
  };

  setupObserver = (root) => {
    if (!this.onVisibleRowsChange || this.observer) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      this.emitAllVisibleOnce();
      return;
    }

    const resolvedRoot = typeof root === 'undefined' ? this.getRoot() : root;
    this.observerRoot = resolvedRoot;
    this.observer = new IntersectionObserver(
      (entries) => {
        let changed = false;

        entries.forEach((entry) => {
          const rowId = this.getRowIdFromElement(entry.target);
          if (!rowId) {
            return;
          }

          const rowIsVisible = this.visibleRowIds.has(rowId);
          if (entry.isIntersecting) {
            if (!rowIsVisible) {
              this.visibleRowIds.add(rowId);
              changed = true;
            }
          } else if (rowIsVisible) {
            this.visibleRowIds.delete(rowId);
            changed = true;
          }
        });

        if (changed) {
          this.emitVisibleRows();
        }
      },
      {
        root: resolvedRoot,
        threshold: 0
      }
    );
  };

  teardown = () => {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.observerRoot = null;
    this.visibleRowIds.clear();
    this.observedRowElements.clear();
    this.lastVisibleRowIds = [];
    this.fallbackEmitted = false;
  };

  sync = () => {
    if (!this.onVisibleRowsChange || !this.observer) {
      return;
    }

    const rowElements = this.getRowElements();
    const currentElements = new Set(rowElements);

    rowElements.forEach((el) => {
      const rowId = this.getRowIdFromElement(el);
      if (!rowId) {
        return;
      }

      if (!this.observedRowElements.has(el)) {
        this.observedRowElements.set(el, rowId);
        this.observer.observe(el);
      }
    });

    Array.from(this.observedRowElements.keys()).forEach((el) => {
      if (!currentElements.has(el)) {
        this.observer.unobserve(el);
        const rowId = this.observedRowElements.get(el);
        this.observedRowElements.delete(el);
        if (rowId && this.visibleRowIds.has(rowId)) {
          this.visibleRowIds.delete(rowId);
        }
      }
    });

    this.emitVisibleRows();
  };

  emitVisibleRows = () => {
    if (!this.onVisibleRowsChange) {
      return;
    }

    const rowElements = this.getRowElements();
    const dataRowElements = rowElements.filter((el) => this.getRowIdFromElement(el));
    const visibleModels = [];
    const visibleIds = [];
    const visibleIndexes = [];

    dataRowElements.forEach((el, idx) => {
      const rowId = this.getRowIdFromElement(el);
      if (!rowId || !this.visibleRowIds.has(rowId)) {
        return;
      }

      const model = this.getModelById(rowId);
      if (model) {
        visibleIds.push(String(rowId));
        visibleModels.push(model);
        const modelIndex = this.getModelIndexById(rowId);
        visibleIndexes.push(modelIndex === null ? idx : modelIndex);
      }
    });

    if (
      visibleIds.length === this.lastVisibleRowIds.length &&
      visibleIds.every((id, idx) => id === this.lastVisibleRowIds[idx])
    ) {
      return;
    }

    this.lastVisibleRowIds = visibleIds;
    const startIndex = visibleIndexes.length ? visibleIndexes[0] : 0;
    const stopIndex = visibleIndexes.length ? visibleIndexes[visibleIndexes.length - 1] : -1;

    this.onVisibleRowsChange({ startIndex, stopIndex, visibleModels });
  };

  emitAllVisibleOnce = () => {
    if (!this.onVisibleRowsChange || this.fallbackEmitted) {
      return;
    }

    const rowElements = this.getRowElements();
    const dataRowElements = rowElements.filter((el) => this.getRowIdFromElement(el));
    const visibleModels = [];
    const visibleIds = [];
    const visibleIndexes = [];

    dataRowElements.forEach((el, idx) => {
      const rowId = this.getRowIdFromElement(el);
      if (!rowId) {
        return;
      }

      const model = this.getModelById(rowId);
      if (model) {
        visibleIds.push(String(rowId));
        visibleModels.push(model);
        const modelIndex = this.getModelIndexById(rowId);
        visibleIndexes.push(modelIndex === null ? idx : modelIndex);
      }
    });

    this.lastVisibleRowIds = visibleIds;
    const startIndex = visibleIndexes.length ? visibleIndexes[0] : 0;
    const stopIndex = visibleIndexes.length ? visibleIndexes[visibleIndexes.length - 1] : -1;

    this.fallbackEmitted = true;
    this.onVisibleRowsChange({ startIndex, stopIndex, visibleModels });
  };

  /**
   * @param {string} rowId
   * @returns {object|null}
   */
  getModelById = (rowId) => {
    if (!rowId || !this.collection) {
      return null;
    }

    if (this.collection.get) {
      const model = this.collection.get(rowId);
      if (model) {
        return model;
      }
    }

    if (this.collection[this.modelField]) {
      const model = this.collection[this.modelField].find((item) => String(item.id) === String(rowId));
      if (model) {
        return model;
      }
    }

    if (this.collection.models) {
      return this.collection.models.find((item) => String(item.id) === String(rowId)) || null;
    }

    return null;
  };

  /**
   * @param {string} rowId
   * @returns {number|null}
   */
  getModelIndexById = (rowId) => {
    if (!rowId || !this.collection) {
      return null;
    }

    if (this.collection[this.modelField]) {
      const index = this.collection[this.modelField].findIndex((item) => String(item.id) === String(rowId));
      return index === -1 ? null : index;
    }

    if (this.collection.models) {
      const index = this.collection.models.findIndex((item) => String(item.id) === String(rowId));
      return index === -1 ? null : index;
    }

    return null;
  };
}
